import React, { useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import PropTypes from "prop-types";
import { Link } from "@quintype/components";
import { MagazineCoverImageCard } from "../../Atoms/MagazineCoverImage";
import { CollectionName } from "../../Atoms/CollectionName";
import { StateProvider } from "../../SharedContext";
import { RightArrow } from "../../Svgs/right-arrow";
import { getMagazinesTimeStamp } from "../../../utils/timestamp-utils";
import { getCategoryAndSubCategory, trackWidgetImpression } from "../../../../ga-utils";
import { getTemplateName } from "../../../../components/utils";
import { ScrollSnap } from "../../Atoms/ScrollSnap";
import { SliderArrowButton } from "../../Atoms/SliderArrow";
import { useDeviceType } from "../../../../components/customHooks/useDeviceType";
import ListingAdSlot from "../../../../components/molecules/ListingAdSlot";

import "./magazine-editions.m.css";

const MagazineEditions = ({
  collection = [],
  config = {},
  limit,
  isStoryPage = false,
  pageType,
  story = {},
  qtConfig = {},
}) => {
  if (!collection.length) return null;
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const isAdfreePage = get(qtState, ["config", "subscriber"]) === "true";
  pageType = isStoryPage ? "story-page" : pageType;
  const handleClick = (position, item = null) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(
        window.location,
        pageType,
        qtConfig,
        story
      );
      window.dataLayer.push({
        event: "dl_story_listing_component_click",
        link_position: position,
        section_name: config?.rowTitle?.toLowerCase(),
        category: category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: isStoryPage ? getTemplateName(story?.["story-template"]) : "listing",
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };
  const {
    theme = "",
    collectionNameTemplate = "",
    showRowTitle = true,
    rowTitle = "Other Issues",
    editionNameFormat = "magazineDate",
    showBottomReadMore,
    showTitleReadMore,
  } = config;
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const noOfRows = Math.min(collection.length, limit);
  const breakPoint = isDesktop ? 15 : 6;

  const componentRef = useRef(null);
  const isBrowser = typeof window !== "undefined";

  useEffect(() => {
    if (!isBrowser || !componentRef.current) return;

    if (isBrowser) {
      window.__magazineEditionsTracked = window.__magazineEditionsTracked || false;
    }

    // Only proceed if not already tracked
    if (window.__magazineEditionsTracked) return;

    const handleImpression = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !window.__magazineEditionsTracked) {
          // Set global flag first to prevent race conditions
          window.__magazineEditionsTracked = true;

          // Do the actual tracking
          trackWidgetImpression({
            sectionName: config?.rowTitle || "Magazine Editions",
            pageType,
            location: window.location,
            config: qtConfig,
            story,
          });

          // Disconnect observer after tracking
          if (observer) {
            observer.disconnect();
          }
        }
      });
    };

    const observer = new IntersectionObserver(handleImpression, {
      threshold: 0.2,
    });

    observer.observe(componentRef.current);

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <div
      className="full-width-with-padding arrow-component arr-magazine-issues"
      style={{ backgroundColor: theme }}
      ref={componentRef}
      data-test-id="editions"
    >
      <div styleName={`container `}>
        {showRowTitle && (
          <div styleName="collection-title-container">
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleClick(-2);
              }}
            >
              <CollectionName
                collection={collection}
                collectionNameTemplate={collectionNameTemplate}
                customCollectionName={rowTitle}
                navigate={true}
                customSlug="/magazine"
              />
            </div>
            <div
              styleName="read-more"
              onClick={(e) => {
                e.stopPropagation();
                handleClick(-1);
              }}
            >
              <Link href="/magazine" disableAjaxLinks={true}>
                {showTitleReadMore && "Read More"} <RightArrow color="#C6554F" />
              </Link>
            </div>
          </div>
        )}
        <div
          className="magazine-editions"
          styleName={`issues ${isStoryPage && isDesktop ? "story-page" : ""}`}
          style={{ "--rows": noOfRows }}
        >
          {isStoryPage && isDesktop ? (
            <ScrollSnap
              isArrow={isDesktop}
              slideIndicator={"none"}
              nextIcon={<SliderArrowButton type="right-arrow" />}
              previousIcon={<SliderArrowButton type="left-arrow" />}
              perView={3}
            >
              {collection.slice(0, limit).map((issue, index) => {
                const {
                  name,
                  "created-at": createdAt,
                  "collection-date": collectionDate,
                  "issue-date": issueDate,
                  metadata,
                } = issue;
                const volNumber = get(metadata, ["volume-number"], "");
                const issueNumber = get(metadata, ["issue-number"], "");
                const date = issueDate || collectionDate || createdAt;
                const magazineDate = isStoryPage
                  ? getMagazinesTimeStamp(date, volNumber, issueNumber)
                  : getMagazinesTimeStamp(date);
                return (
                  <div
                    key={issue.slug}
                    className="magazine-issue"
                    styleName="magazine-issue"
                    data-test-id={`current-issue-${index}`}
                  >
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClick(index + 1, issue);
                      }}
                    >
                      <Link href={`/magazine/${issue.slug}`} styleName="issue-wrapper" disableAjaxLinks={true}>
                        <MagazineCoverImageCard
                          collection={issue}
                          config={{ ...config, isArchivePage: false, useLink: false }}
                        />
                        <div styleName="date" data-test-id="issue-label">
                          {editionNameFormat === "magazineTitle" ? name : magazineDate}
                        </div>
                        <div styleName="magazine-issue-name">{name}</div>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </ScrollSnap>
          ) : (
            <>
              {collection.slice(0, limit).map((issue, index) => {
                const {
                  name,
                  "created-at": createdAt,
                  "collection-date": collectionDate,
                  "issue-date": issueDate,
                } = issue;
                const date = issueDate || collectionDate || createdAt;
                const customPaddingStyleName = () => {
                  if (!isDesktop) return "magazine-issue";

                  const remainder = index % 5;
                  if (remainder === 0) return "magazine-issue left-padding-nil";
                  if (remainder === 4) return "magazine-issue right-padding-nil";

                  return "magazine-issue";
                };
                return (
                  <>
                    <div
                      className={customPaddingStyleName()}
                      styleName="magazine-issue"
                      data-test-id={`current-issue-${index}`}
                      key={index}
                    >
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick(index + 1, issue);
                        }}
                      >
                        <Link
                          href={`/magazine/${issue.slug}`}
                          key={issue.slug}
                          styleName="issue-wrapper"
                          disableAjaxLinks={true}
                        >
                          <MagazineCoverImageCard
                            collection={issue}
                            config={{ ...config, isArchivePage: false, useLink: false }}
                          />
                          <div styleName="date" data-test-id="issue-label">
                            {editionNameFormat === "magazineTitle"
                              ? name
                              : getMagazinesTimeStamp(date, "", "", true, true)}
                          </div>
                          <div styleName="magazine-issue-name">{name}</div>
                        </Link>
                      </div>
                    </div>
                    {index > 0 && (index + 1) % breakPoint === 0 && !isAdfreePage && (
                      <div styleName="ad-wrapper">
                        <ListingAdSlot
                          adStyleName="ad-slot-size-728x90-300x250"
                          adType="grid_ad"
                          index={index}
                          pageType={pageType}
                        />
                      </div>
                    )}
                  </>
                );
              })}
            </>
          )}
        </div>
        {showBottomReadMore && (
          <div
            className="bottom-readmore-button"
            onClick={(e) => {
              e.stopPropagation();
              handleClick(-1);
            }}
          >
            <Link href="/magazine" disableAjaxLinks={true}>
              <div className="arr--button">View More</div>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

MagazineEditions.propTypes = {
  collection: PropTypes.array,
  config: PropTypes.shape({
    rowTitle: PropTypes.string,
    theme: PropTypes.string,
    showRowTitle: PropTypes.bool,
    collectionNameTemplate: PropTypes.string,
    cardType: PropTypes.string,
  }),
  limit: PropTypes.number,
  isLoadMore: PropTypes.bool,
  showLoadmore: PropTypes.bool,
  isStoryPage: PropTypes.bool,
  pageType: PropTypes.string,
  story: PropTypes.object,
  qtConfig: PropTypes.object,
};

export default StateProvider(MagazineEditions);
